import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/bodyScrollLock.js";
import "./walkthrough.css";

const PADDING = 8;
const TOOLTIP_GAP = 12;
const VIEW_MARGIN = 16;
const ESTIMATED_CARD_HEIGHT = 200;
const ESTIMATED_CARD_WIDTH = 360;
const MIN_VISIBLE_SIZE = 8;
const MAX_MEASURE_TRIES = 24;
const MEASURE_RETRY_MS = 80;

function visibleArea(el) {
    const clipped = visibleRect(el.getBoundingClientRect());
    return clipped.width * clipped.height;
}

function resolveTarget(target) {
    if (!target) {
        return null;
    }
    const selector = typeof target === "function" ? target() : target;
    if (!selector) {
        return null;
    }
    const nodes = document.querySelectorAll(selector);
    let best = null;
    let bestArea = 0;
    for (const el of nodes) {
        if (!isMeasurableElement(el)) {
            continue;
        }
        const area = visibleArea(el);
        if (area > bestArea) {
            bestArea = area;
            best = el;
        }
    }
    return best;
}

function isMeasurableElement(el) {
    if (!(el instanceof Element)) {
        return false;
    }
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden" || Number(style.opacity) === 0) {
        return false;
    }
    const clipped = visibleRect(el.getBoundingClientRect());
    return clipped.width >= MIN_VISIBLE_SIZE && clipped.height >= MIN_VISIBLE_SIZE;
}

function isMainNavTourSelector(selector) {
    return typeof selector === "string" && selector.includes('data-tour="nav-');
}

export const WALKTHROUGH_OPEN_MOBILE_NAV_EVENT = "storefront-walkthrough-open-mobile-nav";
export const WALKTHROUGH_CLOSE_MOBILE_NAV_EVENT = "storefront-walkthrough-close-mobile-nav";
export const WALKTHROUGH_MOBILE_NAV_READY_EVENT = "storefront-walkthrough-mobile-nav-ready";

function syncMobileNavForTourTarget(selector) {
    if (typeof window === "undefined") {
        return;
    }
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    if (isMobile && isMainNavTourSelector(selector)) {
        window.dispatchEvent(new Event(WALKTHROUGH_OPEN_MOBILE_NAV_EVENT));
        return;
    }
    window.dispatchEvent(new Event(WALKTHROUGH_CLOSE_MOBILE_NAV_EVENT));
}

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function visibleRect(raw) {
    const top = Math.max(raw.top, 0);
    const left = Math.max(raw.left, 0);
    const bottom = Math.min(raw.top + raw.height, window.innerHeight);
    const right = Math.min(raw.left + raw.width, window.innerWidth);
    return {
        top,
        left,
        width: Math.max(0, right - left),
        height: Math.max(0, bottom - top),
        bottom,
        right
    };
}

/**
 * Keep the walkthrough card fully inside the viewport.
 * @returns {{ mode: "center" } | { mode: "anchored", left: number, top: number }}
 */
function tooltipPlacement(rect, cardWidth = ESTIMATED_CARD_WIDTH, cardHeight = ESTIMATED_CARD_HEIGHT) {
    if (!rect || rect.width <= 0 || rect.height <= 0) {
        return { mode: "center" };
    }

    const width = Math.min(cardWidth, window.innerWidth - VIEW_MARGIN * 2);
    const height = Math.min(cardHeight, window.innerHeight - VIEW_MARGIN * 2);
    const spaceBelow = window.innerHeight - rect.bottom - VIEW_MARGIN;
    const spaceAbove = rect.top - VIEW_MARGIN;
    const spaceRight = window.innerWidth - rect.right - VIEW_MARGIN;
    const fitsBelow = spaceBelow >= height + TOOLTIP_GAP;
    const fitsAbove = spaceAbove >= height + TOOLTIP_GAP;
    const fitsRight = spaceRight >= width + TOOLTIP_GAP;
    const targetTooTall = rect.height > window.innerHeight * 0.45;

    let top;
    let left;

    if (targetTooTall || (!fitsBelow && !fitsAbove)) {
        // Tall panels (filters): dock to the bottom, prefer to the right of the hole.
        top = window.innerHeight - VIEW_MARGIN - height;
        if (fitsRight) {
            left = rect.right + TOOLTIP_GAP;
        } else {
            left = clamp(
                rect.left + rect.width / 2 - width / 2,
                VIEW_MARGIN,
                window.innerWidth - VIEW_MARGIN - width
            );
        }
    } else if (fitsBelow || rect.top < window.innerHeight * 0.45) {
        top = rect.bottom + TOOLTIP_GAP;
        left = clamp(
            rect.left + rect.width / 2 - width / 2,
            VIEW_MARGIN,
            window.innerWidth - VIEW_MARGIN - width
        );
    } else {
        top = rect.top - TOOLTIP_GAP - height;
        left = clamp(
            rect.left + rect.width / 2 - width / 2,
            VIEW_MARGIN,
            window.innerWidth - VIEW_MARGIN - width
        );
    }

    return {
        mode: "anchored",
        left: clamp(left, VIEW_MARGIN, Math.max(VIEW_MARGIN, window.innerWidth - VIEW_MARGIN - width)),
        top: clamp(top, VIEW_MARGIN, Math.max(VIEW_MARGIN, window.innerHeight - VIEW_MARGIN - height))
    };
}

/**
 * Spotlight walkthrough overlay.
 * @param {{ steps: import("./walkthroughSteps.js").WalkthroughStep[], open: boolean, onComplete: () => void, onSkip: () => void }} props
 */
export default function Walkthrough({ steps, open, onComplete, onSkip }) {
    const [index, setIndex] = useState(0);
    const [rect, setRect] = useState(null);
    const [cardSize, setCardSize] = useState({ width: ESTIMATED_CARD_WIDTH, height: ESTIMATED_CARD_HEIGHT });
    const cardRef = useRef(null);

    useEffect(() => {
        if (open) {
            setIndex(0);
        }
    }, [open, steps]);

    useLayoutEffect(() => {
        if (!open || !steps?.length) {
            setRect(null);
            return undefined;
        }

        let cancelled = false;
        let tries = 0;
        let retryId = 0;

        const measure = () => {
            if (cancelled) {
                return;
            }
            const step = steps[index];
            const selector = typeof step?.target === "function" ? step.target() : step?.target;
            syncMobileNavForTourTarget(selector ?? "");
            const el = resolveTarget(step?.target);
            if (el) {
                el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
                const next = el.getBoundingClientRect();
                const hole = visibleRect({
                    top: next.top - PADDING,
                    left: next.left - PADDING,
                    width: next.width + PADDING * 2,
                    height: next.height + PADDING * 2
                });
                if (hole.width >= MIN_VISIBLE_SIZE && hole.height >= MIN_VISIBLE_SIZE) {
                    setRect(hole);
                    return;
                }
            }
            setRect(null);
            if (tries < MAX_MEASURE_TRIES) {
                tries += 1;
                retryId = window.setTimeout(measure, MEASURE_RETRY_MS);
            }
        };

        measure();
        const onResize = () => measure();
        window.addEventListener(WALKTHROUGH_MOBILE_NAV_READY_EVENT, measure);
        window.addEventListener("resize", onResize);
        window.addEventListener("scroll", onResize, true);
        return () => {
            cancelled = true;
            window.clearTimeout(retryId);
            window.removeEventListener(WALKTHROUGH_MOBILE_NAV_READY_EVENT, measure);
            window.removeEventListener("resize", onResize);
            window.removeEventListener("scroll", onResize, true);
            window.dispatchEvent(new Event(WALKTHROUGH_CLOSE_MOBILE_NAV_EVENT));
        };
    }, [open, index, steps]);

    useLayoutEffect(() => {
        if (!open || !cardRef.current) {
            return undefined;
        }
        const node = cardRef.current;
        const syncSize = () => {
            const { width, height } = node.getBoundingClientRect();
            if (width > 0 && height > 0) {
                setCardSize((previous) => (
                    previous.width === width && previous.height === height
                        ? previous
                        : { width, height }
                ));
            }
        };
        syncSize();
        const observer = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(syncSize)
            : null;
        observer?.observe(node);
        return () => observer?.disconnect();
    }, [open, index, steps]);

    useEffect(() => {
        if (!open) {
            document.body.classList.remove("walkthrough-active");
            window.dispatchEvent(new Event(WALKTHROUGH_CLOSE_MOBILE_NAV_EVENT));
            return undefined;
        }
        document.body.classList.add("walkthrough-active");
        lockBodyScroll();
        return () => {
            document.body.classList.remove("walkthrough-active");
            unlockBodyScroll();
        };
    }, [open]);

    if (!open || !steps?.length) {
        return null;
    }

    const step = steps[index];
    const isLast = index >= steps.length - 1;
    const placement = tooltipPlacement(rect, cardSize.width, cardSize.height);
    const holeStyle = rect
        ? {
            top: `${rect.top}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            height: `${rect.height}px`
        }
        : null;

    const goNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setIndex((current) => current + 1);
    };

    return (
        <div className="walkthrough" role="dialog" aria-modal="true" aria-labelledby="walkthrough-title">
            <div className="walkthrough-backdrop" aria-hidden="true">
                {holeStyle ? (
                    <div className="walkthrough-hole" style={holeStyle} />
                ) : (
                    <div className="walkthrough-backdrop-fill" />
                )}
            </div>

            <div
                ref={cardRef}
                className={`walkthrough-card card${placement.mode === "center" ? " walkthrough-card--center" : ""}`}
                style={
                    placement.mode === "center"
                        ? undefined
                        : {
                            left: `${placement.left}px`,
                            top: `${placement.top}px`
                        }
                }
            >
                <div className="walkthrough-card-header">
                    <p className="walkthrough-progress">
                        {index + 1} / {steps.length}
                    </p>
                    <button type="button" className="link-btn" onClick={onSkip}>
                        Passer
                    </button>
                </div>
                <h2 id="walkthrough-title">{step.title}</h2>
                <p className="walkthrough-body">{step.body}</p>
                <div className="walkthrough-actions">
                    <button type="button" className="btn btn-primary" onClick={goNext}>
                        {isLast ? "Terminer" : "Suivant"}
                    </button>
                </div>
            </div>
        </div>
    );
}
